import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import ColorPicker from '@/components/ColorPicker';
const ProductCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#3B82F6',
  });

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/product-categories');
      setCategories(response.data);
    } catch (error) {
      toast.error('Erro ao carregar categorias de produtos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await api.put(`/product-categories/${editingCategory.id}`, formData);
        toast.success('Categoria atualizada!');
      } else {
        await api.post('/product-categories', formData);
        toast.success('Categoria criada!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadCategories();
    } catch (error) {
      toast.error('Erro ao salvar categoria');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja excluir esta categoria de produto?')) return;
    try {
      await api.delete(`/product-categories/${id}`);
      toast.success('Categoria excluída!');
      loadCategories();
    } catch (error) {
      toast.error('Erro ao excluir categoria');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', color: '#3B82F6' });
    setEditingCategory(null);
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setFormData({ name: category.name, color: category.color });
    setIsDialogOpen(true);
  };

  return (
    <div data-testid="product-categories-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Categorias de Produtos
        </h1>
        <p className="text-muted-foreground">Organize seus produtos em categorias específicas</p>
      </div>

      <div className="mb-6">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-product-category-button" className="bg-indigo-600 hover:bg-indigo-700">
              <Plus size={18} className="mr-2" />
              Nova Categoria de Produto
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="product-category-dialog">
            <DialogHeader>
              <DialogTitle>{editingCategory ? 'Editar' : 'Nova'} Categoria de Produto</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nome</Label>
                <Input
                  type="text"
                  data-testid="product-category-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Ex: Camisetas, Kits, Acessórios"
                />
              </div>
              <div>
                <Label>Cor</Label>
                <ColorPicker value={formData.color} onChange={(c) => setFormData({ ...formData, color: c })} testIdPrefix="pcat-color" />
              </div>
              <Button type="submit" data-testid="product-category-submit-button" className="w-full bg-indigo-600 hover:bg-indigo-700">
                {editingCategory ? 'Atualizar' : 'Criar'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-categories-list">
        {loading ? (
          <div className="col-span-full text-center py-8 text-muted-foreground">Carregando...</div>
        ) : categories.length === 0 ? (
          <div className="col-span-full text-center py-8 text-muted-foreground">Nenhuma categoria de produto encontrada</div>
        ) : (
          categories.map((category) => (
            <Card key={category.id} className="hover:shadow-md transition-shadow" data-testid={`product-category-card-${category.id}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: category.color }}
                  >
                    <div className="w-6 h-6 bg-white/30 rounded"></div>
                  </div>
                  <CardTitle className="text-lg">{category.name}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditDialog(category)}
                    data-testid={`edit-product-category-${category.id}`}
                    className="flex-1"
                  >
                    <Pencil size={16} className="mr-2" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(category.id)}
                    data-testid={`delete-product-category-${category.id}`}
                    className="flex-1 text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Excluir
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default ProductCategories;
